/**
 * EGMMS V3 - Project Service
 * 프로젝트 관리 서비스
 */

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase.config';
import { Project, ProjectFormData } from '../types';
import { formatProjectInfo } from '../utils/info-file.utils';
import { createSafeFolderName, getProjectFolderStructure } from '../utils/nas-path.utils';
import unifiedStorageService from './storage/unified-storage.service';

/**
 * 1. 프로젝트 생성
 */
export const createProject = async (data: ProjectFormData): Promise<string> => {
  try {
    // 프로젝트 번호를 ID로 사용
    const projectNumber = data.projectNo || data.projectId;
    
    // 검색 키워드 생성
    const searchTerms = [
      projectNumber.toLowerCase(),
      data.projectName.toLowerCase(),
      data.customerName?.toLowerCase() || ''
    ].filter(term => term);

    // undefined 필드 제거를 위한 기본 데이터
    const projectData: any = {
      projectId: projectNumber,
      projectNo: projectNumber,
      projectName: data.projectName,
      projectType: data.projectType,
      customerId: data.customerId,
      customerName: data.customerName || '',
      status: data.status,
      moldCount: 0,
      moldIds: [],
      hasFiles: false,
      fileCount: 0,
      searchTerms,
      tags: data.tags || [],
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    };

    // 선택적 필드는 값이 있을 때만 추가
    if (data.startDate) {
      projectData.startDate = Timestamp.fromDate(new Date(data.startDate));
    }
    if (data.targetDate) {
      projectData.targetDate = Timestamp.fromDate(new Date(data.targetDate));
    }
    if (data.scheduledDate) {
      projectData.scheduledDate = Timestamp.fromDate(data.scheduledDate);
    }
    if (data.completedDate) {
      projectData.completedDate = Timestamp.fromDate(data.completedDate);
    }
    if (data.notes) {
      projectData.notes = data.notes;
    }
    if (data.priority) {
      projectData.priority = data.priority;
    }
    if (data.description) {
      projectData.description = data.description;
    }

    const docRef = await addDoc(collection(db, 'projects'), projectData);
    
    // ⭐ INFO 파일 생성 (비동기 - 실패해도 프로젝트 생성은 성공)
    try {
      const project: Project = {
        id: docRef.id,
        ...projectData,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };
      
      // Info 파일 내용 생성
      const infoContent = formatProjectInfo(project);
      
      // 폴더명 생성 (프로젝트번호_프로젝트명)
      const folderName = `${createSafeFolderName(projectNumber)}_${createSafeFolderName(data.projectName)}`;
      
      // 1️⃣ 프로젝트 폴더 구조 생성
      const folderPaths = getProjectFolderStructure(docRef.id, projectNumber, data.projectName);
      await unifiedStorageService.createFolderStructure(folderPaths);
      console.log('✅ 프로젝트 폴더 구조 생성 완료:', folderPaths.length, '개');
      
      // 2️⃣ Info 파일 경로 및 파일명 (Firebase 형식)
      const infoFilename = `${folderName}_project_info.txt`;
      const infoPath = `projects/${folderName}/${infoFilename}`;
      
      // 3️⃣ Info 파일 업로드
      await unifiedStorageService.uploadTextFile(infoPath, infoContent, infoFilename);
      
      console.log('✅ 프로젝트 Info 파일 생성 완료:', infoPath);
    } catch (infoError) {
      console.error('⚠️ 프로젝트 Info 파일 생성 실패 (프로젝트는 정상 생성됨):', infoError);
    }
    
    return docRef.id;
  } catch (error) {
    console.error('프로젝트 생성 오류:', error);
    throw error;
  }
};

/**
 * 2. 전체 프로젝트 조회
 */
export const getProjects = async (): Promise<Project[]> => {
  try {
    const q = query(
      collection(db, 'projects'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    const projects = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Project));

    // 각 프로젝트의 실제 금형 수 계산
    const projectsWithMoldCount = await Promise.all(
      projects.map(async (project) => {
        try {
          const moldsQuery = query(
            collection(db, 'molds'),
            where('projectId', '==', project.id)
          );
          const moldsSnapshot = await getDocs(moldsQuery);
          return {
            ...project,
            moldCount: moldsSnapshot.size
          };
        } catch (error) {
          console.error(`프로젝트 ${project.id} 금형 수 조회 실패:`, error);
          return {
            ...project,
            moldCount: 0
          };
        }
      })
    );

    return projectsWithMoldCount;
  } catch (error) {
    console.error('프로젝트 목록 조회 오류:', error);
    throw error;
  }
};

/**
 * 3. 단일 프로젝트 조회
 */
export const getProjectById = async (projectId: string): Promise<Project | null> => {
  try {
    const docRef = doc(db, 'projects', projectId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return {
        id: docSnap.id,
        ...docSnap.data()
      } as Project;
    }
    return null;
  } catch (error) {
    console.error('프로젝트 조회 오류:', error);
    throw error;
  }
};

/**
 * 4. 거래처별 프로젝트 조회
 */
export const getProjectsByCustomerId = async (customerId: string): Promise<Project[]> => {
  try {
    const q = query(
      collection(db, 'projects'),
      where('customerId', '==', customerId),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    } as Project));
  } catch (error) {
    console.error('거래처별 프로젝트 조회 오류:', error);
    throw error;
  }
};


/**
 * 5. 프로젝트 수정
 */
export const updateProject = async (
  projectId: string,
  data: Partial<ProjectFormData>
): Promise<void> => {
  try {
    const docRef = doc(db, 'projects', projectId);
    
    // 업데이트할 데이터 준비
    const updateData: any = {
      updatedAt: serverTimestamp()
    };
    
    // 변경된 필드만 추가
    if (data.projectName) updateData.projectName = data.projectName;
    if (data.projectType) updateData.projectType = data.projectType;
    if (data.customerId) updateData.customerId = data.customerId;
    if (data.customerName) updateData.customerName = data.customerName;
    if (data.status) updateData.status = data.status;
    if (data.notes !== undefined) updateData.notes = data.notes;
    if (data.priority !== undefined) updateData.priority = data.priority;
    if (data.tags) updateData.tags = data.tags;
    if (data.description !== undefined) updateData.description = data.description;
    
    // 날짜 변환 - startDate, targetDate
    if (data.startDate !== undefined) {
      updateData.startDate = data.startDate 
        ? Timestamp.fromDate(new Date(data.startDate)) 
        : null;
    }
    if (data.targetDate !== undefined) {
      updateData.targetDate = data.targetDate 
        ? Timestamp.fromDate(new Date(data.targetDate)) 
        : null;
    }
    
    // 날짜 변환 - scheduledDate, completedDate (호환성)
    if (data.scheduledDate !== undefined) {
      updateData.scheduledDate = data.scheduledDate 
        ? Timestamp.fromDate(data.scheduledDate) 
        : null;
    }
    if (data.completedDate !== undefined) {
      updateData.completedDate = data.completedDate 
        ? Timestamp.fromDate(data.completedDate) 
        : null;
    }
    
    // 검색 키워드 재생성 (필요한 경우)
    if (data.projectName || data.customerName) {
      const project = await getProjectById(projectId);
      if (project) {
        updateData.searchTerms = [
          project.projectId.toLowerCase(),
          (data.projectName || project.projectName).toLowerCase(),
          (data.customerName || project.customerName).toLowerCase()
        ].filter(term => term);
      }
    }
    
    await updateDoc(docRef, updateData);
    
    // ⭐ INFO 파일 업데이트 (비동기 - 실패해도 프로젝트 수정은 성공)
    try {
      // 업데이트된 프로젝트 데이터 조회
      const updatedProject = await getProjectById(projectId);
      if (updatedProject) {
        // Info 파일 내용 생성
        const infoContent = formatProjectInfo(updatedProject);
        
        // 폴더명 생성 (프로젝트번호_프로젝트명)
        const folderName = `${createSafeFolderName(updatedProject.projectNo)}_${createSafeFolderName(updatedProject.projectName)}`;
        
        // Info 파일명 및 경로 (Firebase 형식)
        const infoFilename = `${folderName}_project_info.txt`;
        const infoPath = `projects/${folderName}/${infoFilename}`;
        
        // Info 파일 업로드 (덮어쓰기)
        await unifiedStorageService.uploadTextFile(infoPath, infoContent, infoFilename);
        
        console.log('✅ 프로젝트 Info 파일 업데이트 완료:', infoPath);
      }
    } catch (infoError) {
      console.error('⚠️ 프로젝트 Info 파일 업데이트 실패 (프로젝트는 정상 수정됨):', infoError);
    }
  } catch (error) {
    console.error('프로젝트 수정 오류:', error);
    throw error;
  }
};

/**
 * 6. 프로젝트 삭제
 * 금형이 연결되어 있으면 삭제 불가
 */
export const deleteProject = async (projectId: string): Promise<void> => {
  try {
    // 프로젝트 조회
    const project = await getProjectById(projectId);
    if (!project) {
      throw new Error('프로젝트를 찾을 수 없습니다.');
    }
    
    // 금형이 연결되어 있는지 확인
    if (project.moldIds && project.moldIds.length > 0) {
      throw new Error(
        `프로젝트에 ${project.moldIds.length}개의 금형이 연결되어 있습니다. ` +
        '먼저 금형 연결을 해제해주세요.'
      );
    }
    
    // 삭제 실행
    const docRef = doc(db, 'projects', projectId);
    await deleteDoc(docRef);
  } catch (error) {
    console.error('프로젝트 삭제 오류:', error);
    throw error;
  }
};

/**
 * 7. 프로젝트 번호 중복 체크
 */
export const checkProjectNumber = async (projectNo: string): Promise<boolean> => {
  try {
    const q = query(
      collection(db, 'projects'),
      where('projectNo', '==', projectNo)
    );
    
    const snapshot = await getDocs(q);
    return !snapshot.empty; // true면 중복 있음, false면 사용 가능
  } catch (error) {
    console.error('프로젝트 번호 중복 체크 오류:', error);
    throw error;
  }
};


/**
 * 8. 프로젝트에 금형 추가
 * 양방향 참조 관리 (트랜잭션)
 */
export const addMoldToProject = async (
  projectId: string,
  moldId: string
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    // 프로젝트 조회
    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);
    
    if (!projectSnap.exists()) {
      throw new Error('프로젝트를 찾을 수 없습니다.');
    }
    
    const project = projectSnap.data() as Project;
    
    // 이미 추가되어 있는지 확인
    if (project.moldIds?.includes(moldId)) {
      throw new Error('이미 프로젝트에 추가된 금형입니다.');
    }
    
    // 금형 조회
    const moldRef = doc(db, 'molds', moldId);
    const moldSnap = await getDoc(moldRef);
    
    if (!moldSnap.exists()) {
      throw new Error('금형을 찾을 수 없습니다.');
    }
    
    // 프로젝트 업데이트 (moldIds 배열에 추가, moldCount 증가)
    const updatedMoldIds = [...(project.moldIds || []), moldId];
    batch.update(projectRef, {
      moldIds: updatedMoldIds,
      moldCount: updatedMoldIds.length,
      updatedAt: serverTimestamp()
    });
    
    // 금형 업데이트 (projectId, projectNo 설정)
    batch.update(moldRef, {
      projectId: projectId,
      projectNo: project.projectId,
      updatedAt: serverTimestamp()
    });
    
    // 트랜잭션 커밋
    await batch.commit();
  } catch (error) {
    console.error('프로젝트에 금형 추가 오류:', error);
    throw error;
  }
};

/**
 * 9. 프로젝트에서 금형 제거
 * 양방향 참조 관리 (트랜잭션)
 */
export const removeMoldFromProject = async (
  projectId: string,
  moldId: string
): Promise<void> => {
  try {
    const batch = writeBatch(db);
    
    // 프로젝트 조회
    const projectRef = doc(db, 'projects', projectId);
    const projectSnap = await getDoc(projectRef);
    
    if (!projectSnap.exists()) {
      throw new Error('프로젝트를 찾을 수 없습니다.');
    }
    
    const project = projectSnap.data() as Project;
    
    // 금형이 프로젝트에 포함되어 있는지 확인
    if (!project.moldIds?.includes(moldId)) {
      throw new Error('프로젝트에 포함되지 않은 금형입니다.');
    }
    
    // 프로젝트 업데이트 (moldIds 배열에서 제거, moldCount 감소)
    const updatedMoldIds = project.moldIds.filter(id => id !== moldId);
    batch.update(projectRef, {
      moldIds: updatedMoldIds,
      moldCount: updatedMoldIds.length,
      updatedAt: serverTimestamp()
    });
    
    // 금형 업데이트 (projectId, projectNo 제거)
    const moldRef = doc(db, 'molds', moldId);
    batch.update(moldRef, {
      projectId: null,
      projectNo: null,
      updatedAt: serverTimestamp()
    });
    
    // 트랜잭션 커밋
    await batch.commit();
  } catch (error) {
    console.error('프로젝트에서 금형 제거 오류:', error);
    throw error;
  }
};

/**
 * 10. 프로젝트의 금형 목록 조회
 */
export const getProjectMolds = async (projectId: string): Promise<any[]> => {
  try {
    const q = query(
      collection(db, 'molds'),
      where('projectId', '==', projectId),
      orderBy('moldNumber', 'asc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));
  } catch (error) {
    console.error('프로젝트 금형 목록 조회 오류:', error);
    throw error;
  }
};

// projectService 객체로 export
export const projectService = {
  createProject,
  updateProject,
  deleteProject,
  getProjectById,
  getProjects,
  getProjectsByCustomerId,
  checkProjectNumber,
  addMoldToProject,
  removeMoldFromProject,
  getProjectMolds
};
